import { GroupParticipantsStore } from '@stores/group-participants.store';
import ApiResponse from '@stores/ApiResponse';
import { CohortUser } from '@models/CohortUser';
import { Role, RoleValues } from '@app/enums/Role';
import { Duration } from 'luxon';
import { useQuery } from '@tanstack/react-query';
import { ParticipantView } from '@views';

export class ActiveParticipantsViewCreator {
  constructor(private readonly store = new GroupParticipantsStore()) {}

  async createView(groupId: number): Promise<{
    allCohortUsers: ParticipantView[];
    allActiveCohortUsers: ParticipantView[];
    activeParticipants: ParticipantView[];
  }> {
    const response = await ApiResponse.handleApiResponse<CohortUser[]>(() => this.store.readByGroupId(groupId));
    const allCohortUsers = response.map((u) => {
      return {
        id: u.id,
        name: u.user.name,
        email: u.user.email,
        role: u.role,
        avatarUrl: u.user.avatarUrl,
        CohortUser: u,
        isNew: false,
      } as ParticipantView;
    });
    const allActiveCohortUsers = allCohortUsers.filter((u) => u.CohortUser.isActive);
    const activeParticipants = allActiveCohortUsers.filter(
      (u) => u.CohortUser.isActive && RoleValues[u.CohortUser.role] === Role.Participant
    );
    return {
      allCohortUsers,
      allActiveCohortUsers,
      activeParticipants,
    };
  }
}

// Hook to fetch active participants (legacy)
export const useActiveParticipantsViewCreator = (groupId: number) =>
  useQuery({
    queryKey: [ActiveParticipantsViewCreator.name, groupId],
    queryFn: () => new ActiveParticipantsViewCreator().createView(groupId),
    select: (data) => data.activeParticipants, // For backward compatibility
    staleTime: Duration.fromObject({ minutes: 1 }).milliseconds,
  });

// Hook to fetch all cohort users, active cohort users and active participants
export const useAllCohortUsersViewCreator = (groupId: number) =>
  useQuery({
    queryKey: [ActiveParticipantsViewCreator.name, 'full', groupId],
    queryFn: () => new ActiveParticipantsViewCreator().createView(groupId),
    staleTime: Duration.fromObject({ minutes: 1 }).milliseconds,
  });
